import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import MarketingLayout from "@/components/MarketingLayout";
import Seo, { SITE } from "@/components/Seo";
import { ArrowRight, BookOpen, Clock, FilmSlate } from "@phosphor-icons/react";

export default function GuidesIndex() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/guides").then(({ data }) => setGuides(data.guides || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Group guides into subject sections. Preferred order first, any others appended alphabetically.
  const CAT_ORDER = ["Prepayments", "Accruals", "Deferred income", "Depreciation", "Month-end", "Automation", "Comparisons"];
  const slugifyCat = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  // Learning-path rank within a subject: definitions → how-tos → other → comparisons.
  const guideRank = (g) => {
    const s = (g.slug || "").toLowerCase();
    const t = (g.title || "").toLowerCase();
    if (/^what-is-/.test(s) || /^what is /.test(t)) return 0;                 // definition
    if (/^how-to-/.test(s) || /^how to /.test(t) ||
        /journal|template|example/.test(s)) return 1;                        // how-to / practical
    if ((g.category || "") === "Comparisons" || /-vs-|\bvs\b/.test(s) || / vs /.test(t)) return 3; // comparison
    return 2;                                                                // other explainer
  };
  const grouped = guides.reduce((acc, g) => {
    const cat = g.category || "Other";
    (acc[cat] = acc[cat] || []).push(g);
    return acc;
  }, {});
  Object.values(grouped).forEach((list) =>
    list.sort((a, b) => guideRank(a) - guideRank(b) || a.title.localeCompare(b.title)));
  const sections = Object.keys(grouped).sort((a, b) => {
    const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "WorkWithLottie Month-End Guides",
    url: `${SITE}/guides`,
    blogPost: guides.map((g) => ({
      "@type": "BlogPosting",
      headline: g.title,
      description: g.excerpt,
      url: `${SITE}/guides/${g.slug}`,
    })),
  };

  return (
    <MarketingLayout>
      <Seo title="Month-End Guides" path="/guides"
        description="Practical guides on accruals, prepayments, deferred income and month-end close for accountants and finance teams using Xero."
        jsonLd={jsonLd} />

      <section className="max-w-4xl mx-auto px-6 pt-16 pb-8 text-center">
        <div className="overline mb-4" style={{ color: "#F37A24" }}>Month-End Guides</div>
        <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl tracking-tighter">
          Guides for a faster, cleaner month-end
        </h1>
        <p className="text-navy/70 text-base sm:text-lg mt-5 leading-relaxed">
          Plain-English explainers on accruals, prepayments, deferred income and closing the books on Xero.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-center py-16 text-navy/65" data-testid="guides-loading">Loading…</div>
        ) : guides.length === 0 ? (
          <div className="text-center py-16 text-navy/70" data-testid="guides-empty">No guides published yet — check back soon.</div>
        ) : (
          <>
            {/* Subject jump-nav */}
            <div className="flex flex-wrap justify-center gap-2 mb-14" data-testid="guides-jumpnav">
              {sections.map((cat) => (
                <a key={cat} href={`#cat-${slugifyCat(cat)}`} data-testid={`guides-jump-${slugifyCat(cat)}`}
                  className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 bg-white text-navy/75 hover:border-orange hover:text-orange transition-colors">
                  {cat} <span className="text-navy/40">{grouped[cat].length}</span>
                </a>
              ))}
            </div>

            <div className="space-y-16">
              {sections.map((cat) => (
                <div key={cat} id={`cat-${slugifyCat(cat)}`} className="scroll-mt-24" data-testid={`guides-section-${slugifyCat(cat)}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <BookOpen size={20} weight="duotone" style={{ color: "#F37A24" }} />
                    <h2 className="font-heading font-bold text-navy text-2xl tracking-tight">{cat}</h2>
                    <span className="text-navy/45 text-sm font-medium">{grouped[cat].length} guide{grouped[cat].length === 1 ? "" : "s"}</span>
                    <span className="flex-1 border-t border-slate-200 ml-2" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-testid={`guides-grid-${slugifyCat(cat)}`}>
                    {grouped[cat].map((g) => (
                      <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`}
                        className="group bg-white border border-slate-200 rounded-2xl p-6 shadow-card hover:shadow-lg hover:-translate-y-0.5 transition-all">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "#F37A24" }}>
                            <BookOpen size={14} weight="bold" /> {g.category}
                          </div>
                          {g.has_video && (
                            <span className="inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "rgba(242,111,33,0.12)", color: "#C2410C" }} data-testid={`guide-video-badge-${g.slug}`}>
                              <FilmSlate size={12} weight="fill" /> Video
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-navy text-lg mt-3 leading-snug group-hover:text-orange transition-colors">{g.title}</h3>
                        <p className="text-navy/70 text-sm mt-2 leading-relaxed line-clamp-3">{g.excerpt}</p>
                        <div className="flex items-center justify-between mt-5">
                          <span className="text-xs text-navy/65 flex items-center gap-1"><Clock size={13} /> {g.read_minutes} min read</span>
                          <span className="text-sm font-semibold text-orange flex items-center gap-1">Read <ArrowRight size={14} weight="bold" /></span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </MarketingLayout>
  );
}
